import { Exercise as ComponentExercise, ExerciseType as ComponentExerciseType } from '@/components/exercises/exercise.interfaces';

// Atualizado para corresponder às strings exatas usadas no backend
export enum ExerciseType {
  MultipleChoice = 'multiple_choice',
  FillInTheBlanks = 'fill_in_the_blanks',
  DragAndDrop = 'drag_and_drop',
  Video = 'video',
  ComprehensionQuestion = 'comprehension_question',
  PronunciationPractice = 'pronunciation_practice',
  AudioRecording = 'audio_recording',
  SpeakTheSentence = 'speak_the_sentence',
  WriteTheTranslation = 'write_the_translation',
  FillInTheText = 'fill_in_the_text', // ADICIONADO
  WatchLargeVideo = "watch_large_video",
  FormattedText = "formatted_text",
  StyledSection = "styled_section",
  GlossaryDisplay = "glossary_display"
}

// Type-specific data interfaces - keeping all the different exercise types
export interface MultipleChoiceOption {
  text: string;
  order: number;
  isCorrect: boolean;
}

export interface MultipleChoiceData {
  question: string;
  options: MultipleChoiceOption[];
  multipleCorrectAnswers: boolean;
}

export interface FillInBlanksData {
  text: string;
  blanks: string[];
}

export interface DragAndDropData {
  text: string;
  blanks: string[];
}

// Interface para dados específicos de exercícios de pronúncia (anterior AudioRecording)
export interface PronunciationPracticeData {
  sentences: string[]; // Campo obrigatório conforme schema do backend
  instructions?: string;
  maxDuration?: number;
}

// Alias for backward compatibility
export type AudioRecordingData = PronunciationPracticeData;

// Interface for Video exercise data
export interface VideoData {
  title: string;
  videoUrl: string;
  description?: string;
  instructions?: string;
  video?: string; // Adding the video property to support both ways of referencing the URL
}

// Interface para dados específicos de exercícios de compreensão
export interface ComprehensionQuestionData {
  question_text: string;
  answer?: string;
}

// Interface para dados específicos do exercício de Texto Formatado
export interface FormattedTextData {
  title: string;
  content: string;
  imageUrl?: string;
  imageAlt?: string;
  backgroundColor?: string;
}

// Interface para dados específicos do exercício de Seção Estilizada
export interface StyledSectionData {
  title: string;
  description?: string;
  icon?: string;
  titleColor?: string;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
}

// Interface para dados específicos do exercício de Glossário
export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  order: number;
}

export interface GlossaryTopic {
  id: string;
  title: string;
  terms: GlossaryTerm[];
  order?: number;
}

export interface GlossaryData {
  title: string;
  topics: GlossaryTopic[];
  // Deprecated: manter terms apenas para compatibilidade de leitura
  terms?: GlossaryTerm[];
}

// Each exercise will have a specific data type based on its type field
export type ExerciseData =
  | MultipleChoiceData
  | FillInBlanksData
  | DragAndDropData
  | PronunciationPracticeData
  | VideoData
  | ComprehensionQuestionData
  | FormattedTextData
  | StyledSectionData
  | GlossaryData;

// Core Exercise interface with type and data fields
export interface Exercise {
  id?: string;
  moduleId: string;
  type: ExerciseType;  // This determines what type of exercise it is
  data: ExerciseData;  // This contains the type-specific data
  order?: number;
  title?: string;  // Title of the exercise from API
  description?: string;  // Description of the exercise from API
  /** When false, hidden from student module exercise flow. Defaults to true. */
  isActive?: boolean;
}

export interface CreateExerciseInput {
  moduleId: string;
  type: ExerciseType;
  data: ExerciseData;
  order?: number;
}

export interface UpdateExerciseInput {
  type?: ExerciseType;
  data?: ExerciseData;
  order?: number;
  moduleId?: string;  // Added moduleId field to support updating exercises
  isActive?: boolean;
}

// Mapping between the old and new exercise types
export const exerciseTypeMap: Record<ExerciseType, ComponentExerciseType> = {
  [ExerciseType.MultipleChoice]: ComponentExerciseType.MultipleChoice,
  [ExerciseType.FillInTheBlanks]: ComponentExerciseType.FillInTheBlanks,
  [ExerciseType.FillInTheText]: ComponentExerciseType.FillInTheText, // ADICIONADO
  [ExerciseType.DragAndDrop]: ComponentExerciseType.FillInBlanks,
  [ExerciseType.Video]: ComponentExerciseType.Video,
  [ExerciseType.WatchLargeVideo]: ComponentExerciseType.Video,
  [ExerciseType.ComprehensionQuestion]: ComponentExerciseType.ComprehensionQuestion,
  [ExerciseType.PronunciationPractice]: ComponentExerciseType.PronunciationPractice,
  [ExerciseType.AudioRecording]: ComponentExerciseType.AudioRecording,
  [ExerciseType.SpeakTheSentence]: ComponentExerciseType.SpeakTheSentence,
  [ExerciseType.WriteTheTranslation]: ComponentExerciseType.WriteTranslation,
  [ExerciseType.FormattedText]: ComponentExerciseType.FormattedText, // CORRIGIDO
  [ExerciseType.StyledSection]: ComponentExerciseType.FillInBlanks
};

// Reverse mapping
export const reverseExerciseTypeMap: Record<ComponentExerciseType, ExerciseType> = {
  [ComponentExerciseType.MultipleChoice]: ExerciseType.MultipleChoice,
  [ComponentExerciseType.FillInBlanks]: ExerciseType.FillInTheBlanks,
  [ComponentExerciseType.FillInTheBlanks]: ExerciseType.FillInTheBlanks, // ADICIONADO
  [ComponentExerciseType.FillInTheText]: ExerciseType.FillInTheText, // CORRIGIDO
  [ComponentExerciseType.FormattedText]: ExerciseType.FormattedText, // ADICIONADO
  [ComponentExerciseType.PronunciationPractice]: ExerciseType.PronunciationPractice,
  [ComponentExerciseType.WriteTranslation]: ExerciseType.FillInTheBlanks,
  [ComponentExerciseType.Video]: ExerciseType.Video,
  [ComponentExerciseType.ComprehensionQuestion]: ExerciseType.ComprehensionQuestion,
  [ComponentExerciseType.WriteTheTranslation]: ExerciseType.FillInTheBlanks,
  [ComponentExerciseType.SpeakTheSentence]: ExerciseType.AudioRecording,
  [ComponentExerciseType.AudioRecording]: ExerciseType.AudioRecording,
  [ComponentExerciseType.WatchVideo]: ExerciseType.Video,
  [ComponentExerciseType.WatchLargeVideo]: ExerciseType.WatchLargeVideo,
  [ComponentExerciseType.DragAndDrop]: ExerciseType.DragAndDrop,
};

export interface ExerciseService {
  createExercise(input: CreateExerciseInput): Promise<Exercise>;
  updateExercise(exerciseId: string, input: UpdateExerciseInput): Promise<Exercise>;
  deleteExercise(exerciseId: string, moduleId: string): Promise<void>;
  listExercisesByModule(moduleId: string): Promise<Exercise[]>;
}

// Type guards for data validation
export function isMultipleChoiceData(data: any): data is MultipleChoiceData {
  return (
    data &&
    typeof data.question === 'string' &&
    Array.isArray(data.options) &&
    (data.options.length === 0 ||
      data.options.every(
        (opt: any) =>
          typeof opt.text === 'string' &&
          typeof opt.order === 'number' &&
          typeof opt.isCorrect === 'boolean'
      )) &&
    'multipleCorrectAnswers' in data
  );
}

export function isFillInBlanksData(data: any): data is FillInBlanksData {
  return (
    data &&
    typeof data.text === 'string' &&
    Array.isArray(data.blanks)
  );
}

export function isDragAndDropData(data: any): data is DragAndDropData {
  return (
    data &&
    typeof data.text === 'string' &&
    Array.isArray(data.blanks)
  );
}

export function isPronunciationPracticeData(data: any): data is PronunciationPracticeData {
  return (
    data &&
    Array.isArray(data.sentences) && data.sentences.length > 0
  );
}

export function isVideoData(data: any): data is VideoData {
  return (
    data &&
    typeof data.title === 'string' &&
    typeof data.videoUrl === 'string'
  );
}

// Type guards for new data types
export function isComprehensionQuestionData(data: any): data is ComprehensionQuestionData {
  return (
    data &&
    typeof data.question_text === 'string'
  );
}

// Type guards para novos tipos de exercícios
export function isFormattedTextData(data: any): data is FormattedTextData {
  return (
    data &&
    typeof data.title === 'string' &&
    typeof data.content === 'string'
  );
}

export function isStyledSectionData(data: any): data is StyledSectionData {
  return (
    data &&
    typeof data.title === 'string'
  );
}

// Type guards for various exercise data types
export function determineExerciseType(data: any): ExerciseType {
  if (isMultipleChoiceData(data)) {
    return ExerciseType.MultipleChoice;
  } else if (isFillInBlanksData(data)) {
    return ExerciseType.FillInTheBlanks;
  } else if (isDragAndDropData(data)) {
    return ExerciseType.DragAndDrop;
  } else if (isPronunciationPracticeData(data)) {
    return ExerciseType.PronunciationPractice;
  } else if (isVideoData(data)) {
    return ExerciseType.Video;
  } else if (isComprehensionQuestionData(data)) {
    return ExerciseType.ComprehensionQuestion;
  } else if (isFormattedTextData(data)) {
    return ExerciseType.FormattedText;
  } else if (isStyledSectionData(data)) {
    return ExerciseType.StyledSection;
  } else {
    console.warn('Unknown exercise data type:', data);
    return ExerciseType.MultipleChoice; // Fallback
  }
}

// Conversion functions
export function convertToComponentExercise(exercise: Exercise): ComponentExercise {
  const newType = exerciseTypeMap[exercise.type];
  const baseExercise = {
    id: exercise.id || '',
    type: newType,
    data: exercise.data,
    title: exercise.title,
    description: exercise.description
  };

  // Handle each exercise type specifically
  switch (exercise.type) {
    case ExerciseType.MultipleChoice: {
      const data = exercise.data as MultipleChoiceData;
      if (!data) {
        throw new Error(`Invalid data for multiple choice exercise: ${JSON.stringify(data)}`);
      }

      return {
        ...baseExercise,
        data: data
      };
    }

    case ExerciseType.FillInTheBlanks:
    case ExerciseType.FillInTheText:
    case ExerciseType.DragAndDrop: {
      const data = exercise.data as FillInBlanksData;
      return {
        ...baseExercise,
        text: data.text,
        blanks: data.blanks
      };
    }

    case ExerciseType.PronunciationPractice: {
      const data = exercise.data as PronunciationPracticeData;
      // Verificar se data existe
      if (!data) {
        return {
          ...baseExercise,
          sentence: "Exemplo de frase para pronuncie",
          instructions: "Grave o áudio pronunciando a frase",
          maxDuration: 30
        };
      }
      
      // Determinar a sentença a ser usada - sempre usar a primeira do array sentences
      let sentence = '';
      if (data.sentences && Array.isArray(data.sentences) && data.sentences.length > 0) {
        // Usar a primeira sentença do array, removendo aspas se houver
        sentence = data.sentences[0].replace(/^"|"$/g, '');
      } else {
        sentence = "Exemplo de frase para pronuncie";
      }
      
      return {
        ...baseExercise,
        sentence,
        instructions: data.instructions || "Grave o áudio pronunciando a frase",
        maxDuration: data.maxDuration || 30
      };
    }

    case ExerciseType.WatchLargeVideo:
    case ExerciseType.Video: {
      const data = exercise.data as VideoData;
      return {
        ...baseExercise,
        title: data.title,
        videoUrl: data.videoUrl,
        description: data.description || "",
        instructions: data.instructions || "Assista ao vídeo"
      };
    }

    case ExerciseType.ComprehensionQuestion: {
      const data = exercise.data as ComprehensionQuestionData;
      return {
        ...baseExercise,
        question: data.question_text,
        options: [
          { id: "0", text: data.answer || "", isCorrect: true }, 
          { id: "1", text: "Opção incorreta", isCorrect: false }
        ]
      };
    }

    case ExerciseType.FormattedText: {
      // Para FormattedText, retorna com os dados como estão para permitir parsing de JSON
      return {
        ...baseExercise,
        data: exercise.data
      };
    }
    
    case ExerciseType.StyledSection: {
      const data = exercise.data as FormattedTextData;
      return {
        ...baseExercise,
        text: data.content || data.title,
        blanks: []
      };
    }

    case ExerciseType.GlossaryDisplay: {
      // Para GlossaryDisplay, retorna com os dados como estão para permitir parsing de JSON
      return {
        ...baseExercise,
        data: exercise.data
      };
    }

    default:
      console.warn(`Unsupported exercise type: ${exercise.type}`);
      return {
        ...baseExercise,
        type: ComponentExerciseType.MultipleChoice,
        question: "",
        options: []
      };
  }
}

export function convertFromComponentExercise(exercise: ComponentExercise): Exercise {
  
  // Extrair o id e tratar os diferentes casos
  let id: string | undefined = undefined;
  if (exercise.id !== undefined) {
    id = exercise.id.toString();
  }
  
  const oldType = reverseExerciseTypeMap[exercise.type];
  const baseExercise = {
    id,
    type: oldType,
    moduleId: '' // This will need to be set by the caller
  };

  // Create the appropriate data object based on the exercise type
  let data: ExerciseData;

  switch (exercise.type) {
    case ComponentExerciseType.MultipleChoice:
      
      // Verificar se os dados estão no nível raiz ou dentro de 'data'
      let question = '';
      let options: any[] = [];
      
      if (exercise.data && typeof exercise.data === 'object' && 'question' in exercise.data) {
        // Dados já estão estruturados em 'data' (segunda chamada)
        const dataObj = exercise.data as any;
        question = dataObj.question || '';
        options = dataObj.options || [];
      } else {
        // Dados estão no nível raiz (primeira chamada)
        question = exercise.question || '';
        options = exercise.options || [];
      }
      
      data = {
        question,
        options: options.map((opt: any, index: number) => ({
          text: opt.text || opt.Text || '',
          isCorrect: (opt.isCorrect === true) || (opt.IsCorrect === true),
          order: opt.order !== undefined ? opt.order : opt.Order !== undefined ? opt.Order : index
        })),
        multipleCorrectAnswers: (options.filter((opt: any) => (opt.isCorrect === true) || (opt.IsCorrect === true)).length || 0) > 1
      };
      
      return { ...baseExercise, data };

    case ComponentExerciseType.FillInBlanks:
      data = {
        text: exercise.text || "",
        blanks: exercise.blanks || []
      };
      return { ...baseExercise, data };

    case ComponentExerciseType.PronunciationPractice:
      // Verifica se é um exercício de prática de pronúncia pelo contexto
      if (exercise.sentence && exercise.sentence.length > 0) {
        data = {
          sentences: [exercise.sentence], // Converte para array de sentenças
          instructions: exercise.instructions || "Grave o áudio pronunciando a frase",
          maxDuration: exercise.maxDuration || 30
        } as PronunciationPracticeData;
        return { 
          ...baseExercise, 
          type: ExerciseType.PronunciationPractice,
          data 
        };
      } else {
        data = {
          sentences: [""], // Array vazio ou com string vazia
          instructions: exercise.instructions || "Grave o áudio pronunciando a frase",
          maxDuration: exercise.maxDuration || 30
        } as PronunciationPracticeData;
        return { ...baseExercise, data };
      }

    case ComponentExerciseType.WriteTranslation:
      data = {
        originalText: exercise.originalText || "",
        translatedText: exercise.translatedText || "",
      } as any;
      return { ...baseExercise, data };
      
    case ComponentExerciseType.Video:
      data = {
        title: exercise.title || "",
        videoUrl: exercise.videoUrl || "",
        description: exercise.description || "",
        instructions: exercise.instructions || "Assista ao vídeo"
      };
      return { ...baseExercise, data };
      
    case ComponentExerciseType.ComprehensionQuestion:
      data = {
        question_text: exercise.question || "",
        answer: exercise.answer || exercise.options?.find(opt => opt.isCorrect)?.text || ""
      } as ComprehensionQuestionData;
      return {
        ...baseExercise,
        type: ExerciseType.ComprehensionQuestion,
        data
      };

    case ComponentExerciseType.GlossaryDisplay:
      // Para GlossaryDisplay, usar os dados diretamente do exercise.data
      // O adapter no ExerciseForm já garante que os dados estão no formato correto
      data = exercise.data as GlossaryData;
      return {
        ...baseExercise,
        type: ExerciseType.GlossaryDisplay,
        data
      };

    default:
      console.warn(`Tipo de exercício não suportado: ${exercise.type}`);
      return {
        ...baseExercise,
        data: {
          question: exercise.question || '',
          options: exercise.options || [],
          multipleCorrectAnswers: false
        }
      };
  }
}

// Nova função para converter Exercise (do ExerciseDialog) para o formato da API
export function convertExerciseToApiFormat(exercise: any): Exercise {
  let id: string | undefined = undefined;
  if (exercise.id !== undefined) {
    id = exercise.id.toString();
  }
  
  const oldType = reverseExerciseTypeMap[exercise.type];
  const baseExercise = {
    id,
    type: oldType,
    moduleId: '' // This will need to be set by the caller
  };

  // Create the appropriate data object based on the exercise type
  let data: ExerciseData;

  switch (exercise.type) {
    case 'multiple_choice':
      data = {
        question: exercise.question || '',
        options: exercise.options?.map((option: any, index: number) => ({
          text: option.text || '',
          isCorrect: option.isCorrect || false,
          order: index,
        })) || [],
        multipleCorrectAnswers: exercise.multipleCorrectAnswers || false,
      } as MultipleChoiceData;
      break;
      
    case 'write_the_translation':
      data = {
        originalText: exercise.originalText || '',
        translatedText: exercise.translatedText || '',
      } as any;
      break;
      
    default:
      data = {
        question: exercise.question || '',
        options: exercise.options?.map((option: any, index: number) => ({
          text: option.text || '',
          isCorrect: option.isCorrect || false,
          order: index,
        })) || [],
        multipleCorrectAnswers: exercise.multipleCorrectAnswers || false,
      } as MultipleChoiceData;
      break;
  }

  return {
    ...baseExercise,
    data,
    order: exercise.order || 1,
  };
}